import Breadcrumb from '@/components/breadcrumb';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useStore } from '@nanostores/react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { KnowledgeBaseListing } from './components/KnowledgeBaseListing';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateKnowledgeBaseRequest, KnowledgeBaseResult, KnowledgeBaseService } from '@/api';

export type ManageKnowledgeBasesProps = {
  organizationId: string;
};

export const ManageKnowledgeBases = ({ organizationId }: ManageKnowledgeBasesProps) => {
  const knowledgebases = useStore($knowledgebases);
  const queryClient = useQueryClient();
  const [showDrawer, setShowDrawer] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [knowledgeBaseFileToUpload, setKnowledgeBaseFileToUpload] = useState<
    File | undefined
  >();
  // mutation to upload a knowledge base
  const uploadKnowledgeBase = useMutation({
    mutationFn: async (file: File) => {
      // call the API to upload the knowledge base
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      const request: CreateKnowledgeBaseRequest = {
        name: file.name,
        description: '',
        organizationId,
        fileName: file.name,
        fileContentBase64: await file2Base64(file),
      };
      const res = await KnowledgeBaseService.createKnowledgeBase(request);
      return res;
    },
    onSuccess: data => {
     // invalidate queries here so that they are refetched
      queryClient.invalidateQueries({
        queryKey: ['getKnowledgeBases', organizationId],
      });
      if (data.knowledgeBase) {
        const newKnowledgeBase: KnowledgeBaseResult = {
          knowledgeBase: data.knowledgeBase,
        };
        $knowledgebases.set([newKnowledgeBase, ...knowledgebases]);
      }
      toast('File uploaded successfully!', {
        duration: 5000,
      });
    },
    onError: error => {
      toast.error(`Failed to upload file :: ${error.message}`, {
        duration: 5000,
      });
    },
  });

  function uploadFile() {
    if (!knowledgeBaseFileToUpload) return;
    setUploadingFile(true);
    uploadKnowledgeBase.mutate(knowledgeBaseFileToUpload);
    setUploadingFile(false);
  }


  // function uploadFile() {
  //   if (!knowledgeBaseFileToUpload) return;
  //   setUploadingFile(true);
  //   if (org?.id) {
  //     createKnowledgeBase(org?.id, knowledgeBaseFileToUpload, knowledgeBaseFileToUpload.name)
  //       .then(res => {
  //         if (res?.succeeded) {
  //           toast('File uploaded successfully!', {
  //             duration: 5000,
  //           });
  //         } else {
  //           toast.error(`Failed to upload file :: ${res?.message}`, {
  //             duration: 5000,
  //           });
  //         }
  //       })
  //       .catch(() => {
  //         toast.error(`Failed to upload file.`, {
  //           duration: 5000,
  //         });
  //       })
  //       .finally(() => {
  //         setUploadingFile(false);
  //       });
  //   }
  // }


  const org = useStore($org);

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/knowledgebases`
        : '/organizations',
    },
    {
      label: 'KnowledgeBases',
      href: `/organization/${org?.id}/knowledgebases`,
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>

      {/* Page Header with actions */}
      <div className=" mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Knowledge Bases</h2>
          <p className="text-muted-foreground">
            {`List of Knowledge Bases for ${org?.name}.`}
          </p>
        </div>
      </div>
      <div className="grid h-[80px] w-8/12 items-center gap-2 mt-4">
        <Label htmlFor="csvfile">Upload a file to the knowledge base</Label>
          <div className="flex h-full items-center justify-start gap-2">
            <Input
              id="csvfile"
              type="file"
              className="h-full w-1/4 items-start justify-start rounded-lg bg-slate-100 p-4 text-center text-foreground dark:text-primary-foreground"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  setKnowledgeBaseFileToUpload(file);
                }
              }}
            />
            <Button
              className="h-[70%] w-[10%] rounded-lg"
              onClick={() => {
                uploadFile();
              }}
              disabled={uploadingFile}
            >
              {uploadingFile ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
      </div>
      {/* Workspace list */}
      <div className="mt-4 max-w-lg">
        <KnowledgeBaseListing organizationId={org?.id || organizationId} />
      </div>
    </div>
  );
};
